import React, { useState, useEffect } from 'react';
import ImageUpload from './ImageUpload';
import { useData } from '../context/DataContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Award, Plus, Trash2, Edit3, MapPin, Calendar, X } from 'lucide-react';
import { db } from '../firebase';
import { collection, addDoc, setDoc, doc, deleteDoc } from 'firebase/firestore';
import Swal from 'sweetalert2';

const AdminAwards = () => {
  const { data } = useData();
  const awardsList = Array.isArray(data.awards) 
    ? data.awards 
    : [...(data.awards?.national || []), ...(data.awards?.state || []), ...(data.awards?.safety || [])];
  
  const [editingAward, setEditingAward] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState('');

  useEffect(() => {
    if (editingAward && editingAward.data) {
      setUploadedImageUrl(editingAward.data.image || '');
      document.body.classList.add('admin-modal-open');
    } else {
      document.body.classList.remove('admin-modal-open');
    }
    return () => document.body.classList.remove('admin-modal-open');
  }, [editingAward]);

  const handleSaveAward = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const awardData = {
      title: formData.get('title'),
      desc: formData.get('desc'),
      presenter: formData.get('presenter'),
      year: formData.get('year'),
      image: uploadedImageUrl
    };

    Swal.fire({
      title: 'Saving Award...',
      text: 'Recording achievement',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      if (editingAward.data?.id) {
        await setDoc(doc(db, 'awards', editingAward.data.id), awardData);
      } else {
        await addDoc(collection(db, 'awards'), awardData);
      }
      
      await Swal.fire({
        title: 'Success',
        text: 'Award record saved successfully',
        icon: 'success',
        confirmButtonColor: '#2d6a4f',
      });
      setEditingAward(null);
    } catch (error) {
      console.error("Error saving award:", error);
      Swal.fire('Error', 'Failed to save award record', 'error');
    }
  };

  const handleDeleteAward = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await deleteDoc(doc(db, 'awards', id));
        Swal.fire('Deleted!', 'Award record has been deleted.', 'success');
      } catch (error) {
        Swal.fire('Error', 'Failed to delete award record', 'error');
      }
    }
  };

  return (
    <div className="space-y-8 pb-20 font-poppins">
      
      {/* Header Banner */}
      <div className="bg-sugarcane-charcoal p-6 rounded-2xl text-white flex flex-col md:flex-row justify-between items-center gap-6 relative overflow-hidden shadow-md">
        <div className="absolute top-0 left-0 w-64 h-64 bg-sugarcane-green/10 rounded-full -ml-32 -mt-32 blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-emerald-700/10 rounded-full -mr-32 -mb-32 blur-3xl"></div>
        
        <div className="relative z-10">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-10 h-10 bg-sugarcane-green rounded-xl flex items-center justify-center text-white">
              <Award size={20} />
            </div>
            <h2 className="text-2xl font-bold tracking-tight">Awards Registry</h2>
          </div>
          <p className="text-gray-400 text-sm italic">Manage organization achievements and national certificates.</p>
        </div>

        <div className="relative z-10 flex gap-4">
          <button
            onClick={() => setEditingAward({ data: {} })}
            className="bg-sugarcane-green hover:bg-white hover:text-sugarcane-charcoal text-white px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center space-x-2 transition-all shadow-md cursor-pointer"
          >
            <Plus size={18} />
            <span>New Achievement</span>
          </button>
        </div>
      </div>

      {/* Unified Awards Cards Grid */}
      {awardsList.length === 0 ? (
        <div className="bg-white py-20 text-center max-w-sm mx-auto rounded-2xl shadow-sm border border-gray-100">
          <div className="bg-gray-50 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-gray-100 shadow-inner">
            <Award className="text-gray-300" size={28} />
          </div>
          <h4 className="text-md font-bold text-sugarcane-charcoal mb-1">No Achievements Recorded</h4>
          <p className="text-xs text-gray-400 leading-relaxed px-6">
            There are currently no awards logged. Use the button above to record one.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode='popLayout'>
            {awardsList.map((award) => (
              <motion.div
                key={award.id}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3 }}
                className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 flex flex-col justify-between group hover:shadow-md transition-all duration-300 relative h-96"
              >
                <div>
                  {/* Image container */}
                  <div className="relative h-44 overflow-hidden bg-gray-50 flex-shrink-0 border-b border-gray-100">
                    <img 
                      src={award.image || 'https://via.placeholder.com/300x180'} 
                      alt={award.title} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" 
                    />
                  </div>

                  {/* Content details */}
                  <div className="p-5 space-y-3">
                    <h4 className="font-bold text-base text-sugarcane-charcoal line-clamp-1 group-hover:text-sugarcane-green transition-colors">
                      {award.title}
                    </h4>
                    <div className="flex items-center space-x-3 text-[10px] font-extrabold uppercase tracking-wider text-sugarcane-green">
                      <span className="flex items-center bg-sugarcane-green/5 px-2.5 py-1 rounded-md">
                        <MapPin size={10} className="mr-1 text-sugarcane-green" /> {award.presenter}
                      </span>
                      <span className="flex items-center bg-sugarcane-green/5 px-2.5 py-1 rounded-md">
                        <Calendar size={10} className="mr-1 text-sugarcane-green" /> {award.year}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed italic">
                      "{award.desc}"
                    </p>
                  </div>
                </div>

                {/* Footer Action buttons */}
                <div className="p-5 border-t border-gray-100 flex items-center justify-between mt-auto">
                  <button 
                    onClick={() => setEditingAward({ data: award })} 
                    className="text-xs font-bold text-blue-600 hover:text-blue-800 flex items-center space-x-1.5 transition-colors cursor-pointer"
                  >
                    <Edit3 size={14} />
                    <span>Edit Entry</span>
                  </button>
                  <button 
                    onClick={() => handleDeleteAward(award.id)} 
                    className="text-xs font-bold text-red-600 hover:text-red-800 flex items-center space-x-1.5 transition-colors cursor-pointer"
                  >
                    <Trash2 size={14} />
                    <span>Delete Record</span>
                  </button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Unified Award Editor modal */}
      <AnimatePresence>
        {editingAward && (
          <div className="fixed inset-0 z-[1050] flex items-center justify-center p-4 bg-sugarcane-charcoal/80 backdrop-blur-md admin-modal-overlay">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl w-full max-w-5xl shadow-2xl relative max-h-[90vh] overflow-y-auto border border-gray-100"
            >
              <div className="sticky top-0 bg-white/95 backdrop-blur-md px-6 py-5 border-b border-gray-100 flex justify-between items-center z-10">
                <div>
                  <span className="px-3 py-1 bg-sugarcane-green/5 rounded-full text-[9px] font-bold uppercase tracking-widest text-sugarcane-green border border-sugarcane-green/10">
                    Awards Achievement
                  </span>
                  <h3 className="text-xl font-bold text-sugarcane-charcoal mt-1.5">
                    {editingAward.data.id ? 'Edit Award Record' : 'Log New Award Achievement'}
                  </h3>
                </div>
                <button onClick={() => setEditingAward(null)} className="p-2 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors cursor-pointer">
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={handleSaveAward} className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
                  
                  {/* Left columns (Form details) */}
                  <div className="md:col-span-2 space-y-5">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Award Title</label>
                      <input name="title" defaultValue={editingAward.data.title} required className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all outline-none text-sugarcane-charcoal shadow-inner" placeholder="e.g. Best Industrial Unit 2023" />
                    </div>
                    <div className="grid grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Presented By</label>
                        <input name="presenter" defaultValue={editingAward.data.presenter} required className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all outline-none text-sugarcane-charcoal shadow-inner" placeholder="e.g. Govt of Maharashtra" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Month & Year</label>
                        <input name="year" defaultValue={editingAward.data.year} required className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all outline-none text-sugarcane-charcoal shadow-inner" placeholder="e.g. June 2026" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Short Description</label>
                      <textarea name="desc" defaultValue={editingAward.data.desc} required rows="3" className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all outline-none text-sugarcane-charcoal shadow-inner resize-none h-24" placeholder="Brief details about the achievement parameters..."></textarea>
                    </div>
                  </div>

                  {/* Right Column (Image upload footprint) */}
                  <div className="md:col-span-1 bg-gray-50 border border-gray-200 rounded-2xl p-6 flex flex-col justify-center items-center shadow-sm">
                    <div className="w-full max-w-[240px] mx-auto space-y-4">
                      <div className="text-center mb-2">
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-400">Award Image</label>
                        <p className="text-[10px] text-gray-400 mt-1 font-medium">Recommended 1:1 format</p>
                      </div>
                      <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm bg-white">
                        <ImageUpload 
                          initialImage={uploadedImageUrl} 
                          onUploadSuccess={setUploadedImageUrl} 
                        />
                      </div>
                    </div>
                  </div>

                </div>
                <div className="flex justify-end gap-3 pt-6 border-t border-gray-100">
                  <button type="button" onClick={() => setEditingAward(null)} className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl font-bold text-sm transition-all cursor-pointer">
                    Cancel
                  </button>
                  <button type="submit" className="px-6 py-2.5 bg-sugarcane-green hover:bg-sugarcane-charcoal text-white rounded-xl font-bold text-sm transition-all shadow-md shadow-sugarcane-green/10 flex items-center gap-2 cursor-pointer">
                    <Plus size={16} />
                    <span>{editingAward.data.id ? 'Save Changes' : 'Confirm Achievement'}</span>
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminAwards;
